// R8 — copy handler (retelling pane only). A `copy` listener on the retelling
// container appends a copyright + sourcing note to the clipboard text and
// truncates very long selections (SPEC "Copyright protections"). Deliberately
// NO selection-blocking anywhere — selection is load-bearing (it drives the
// phase-6 original-pane popover, and per-pane isolation is a natural property of
// the two-scroll-container layout). These are deterrents and attribution, not
// DRM; the static corpus JSON is necessarily publicly fetchable.

import { useEffect, useRef, type RefObject } from "react";
import { sourcingNote } from "@/lib/copyright";
import { ORIGINAL_SCOPE } from "@bindery/reader-dom/dom-offsets";

/** Selections longer than this are truncated on copy. */
const MAX_COPY_CHARS = 2000;

/**
 * In the mobile interleaved column the original's stretches live INSIDE the
 * retelling's container, so "is the selection in this container" no longer
 * means "is this our prose". Copying a public-domain original must not be
 * stamped with the retelling's copyright note, so those subtrees are excluded
 * and fall through to the browser's plain copy. A no-op on desktop, where the
 * original is a separate container this listener never sees.
 */
function inOriginal(node: Node): boolean {
  const el =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node.parentElement;
  return el?.closest(ORIGINAL_SCOPE) != null;
}

interface UseCopyHandlerArgs {
  retellingRef: RefObject<HTMLDivElement | null>;
  bookTitle: string;
  /**
   * Identifies WHICH component renders the retelling column. `DocumentPane`
   * and `InterleavedColumn` sit at the same tree position, so switching
   * between them mounts a NEW node under the same ref. Without it in the deps
   * the `copy` listener stays bound to the destroyed node and every later copy
   * of retold prose goes out with no copyright note at all. Omit it where the
   * container is rendered unconditionally and so never replaced (the matter
   * view).
   */
  columnKey?: string;
  /** Notified after a copy is handled (for `reading.copy` logging). */
  onCopy?: (info: { length: number; truncated: boolean }) => void;
}

/** The sourcing note for the page being read, with the retrieval URL filled in. */
function sourcingNoteHere(bookTitle: string): string {
  const url = `${window.location.origin}${window.location.pathname}`;
  return sourcingNote(bookTitle, url);
}

export function useCopyHandler({
  retellingRef,
  bookTitle,
  columnKey,
  onCopy,
}: UseCopyHandlerArgs): void {
  // Keep the latest callback in a ref so an inline `onCopy` doesn't re-run the
  // effect and rebind the listener on every render (useEffect discipline 4.3).
  const onCopyRef = useRef(onCopy);
  useEffect(() => {
    onCopyRef.current = onCopy;
  });

  useEffect(() => {
    const container = retellingRef.current;
    if (!container) return;

    const onCopyEvent = (event: ClipboardEvent): void => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return;
      }
      // Test the RANGE, not `anchorNode`: the anchor is wherever the drag
      // STARTED, so a selection begun inside an original inset and dragged up
      // into retold prose — or one running from inset A through retold prose
      // into inset B — used to take the exemption below and copy the retelling
      // unstamped. A selection wholly inside one inset has its common ancestor
      // inside that inset (the public-domain exemption, intact); anything
      // touching retold prose has one at or above the article, and is stamped.
      const common = selection.getRangeAt(0).commonAncestorContainer;
      // Only act on selections that live inside the retelling's own prose —
      // or that swallow the whole container, which necessarily includes it.
      if (!container.contains(common) && !common.contains(container)) return;
      if (inOriginal(common)) return;

      let text = selection.toString();
      if (!text) return;
      const truncated = text.length > MAX_COPY_CHARS;
      if (truncated) {
        text = `${text.slice(0, MAX_COPY_CHARS).trimEnd()}…`;
      }

      event.clipboardData?.setData(
        "text/plain",
        text + sourcingNoteHere(bookTitle),
      );
      event.preventDefault();
      onCopyRef.current?.({ length: text.length, truncated });
    };

    container.addEventListener("copy", onCopyEvent);
    return () => {
      container.removeEventListener("copy", onCopyEvent);
    };
  }, [retellingRef, bookTitle, columnKey]);
}
